import React, { useState } from "react";
import { FiX, FiCheck, FiLoader } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { startCheckout, openBillingPortal, PAID_CHECKOUT_ENABLED } from "../lib/billing";
import "./PlanModal.css";

const TIER_RANK = { free: 0, light: 2, business: 4 };
const CONTACT_EMAIL = "sales@frontplay.dev";

export default function PlanModal({ open, onClose }) {
  const { isLoggedIn, session, tier, openLoginModal, openUsageModal } = useAuth();
  const { texts } = useLanguage();
  const t = texts.billing || {};
  const p = texts.landing?.plans || {};
  const comingSoon = texts.ai?.comingSoon || t.comingSoon || "準備中";
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  if (!open) return null;

  const run = async (fn, key) => {
    setBusy(key);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err.message || t.failed || "Failed");
      setBusy("");
    }
  };

  const handleSelect = async (plan) => {
    if (plan === "business" || plan === "free") {
      if (plan === "free") onClose?.();
      return;
    }
    if (!PAID_CHECKOUT_ENABLED) return;
    if (!isLoggedIn) {
      onClose?.();
      openLoginModal();
      return;
    }
    if (tier === plan) {
      onClose?.();
      return;
    }
    const currentRank = TIER_RANK[tier] ?? 0;
    const nextRank = TIER_RANK[plan] ?? 0;
    if (currentRank > 0 && nextRank < currentRank) {
      setError(t.downgradeViaPortal || "プラン変更・解約は請求ポータルから行えます。");
      return;
    }
    await run(
      () =>
        startCheckout({
          plan,
          accessToken: session?.access_token,
          returnUrl: window.location.origin,
        }),
      plan
    );
  };

  const handleManage = async () => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    await run(
      () =>
        openBillingPortal({
          accessToken: session?.access_token,
          returnUrl: window.location.origin,
        }),
      "portal"
    );
  };

  const paidCta = (id, upgradeKey, fallback) => {
    if (id === "business") return t.contactSales || "お問い合わせ";
    if (tier === id) return t.currentPlan || "現在のプラン";
    if (!PAID_CHECKOUT_ENABLED) return comingSoon;
    if ((TIER_RANK[tier] ?? 0) > (TIER_RANK[id] ?? 0)) return t.manage || "管理";
    return t[upgradeKey] || fallback;
  };

  const cards = [
    {
      id: "free",
      name: p.free?.name || "Free",
      price: p.free?.priceLabel || "$0",
      features: p.free?.features || [],
      cta: tier === "free" ? t.currentPlan || "現在のプラン" : p.free?.cta || "Free",
      disabled: tier === "free" || Boolean(busy),
    },
    {
      id: "light",
      name: p.light?.name || "Pro",
      price: p.light?.priceLabel || "$2/mo",
      badge: p.light?.badge,
      features: p.light?.features || [],
      cta: paidCta("light", "upgradeToLight", "Pro にアップグレード"),
      disabled: !PAID_CHECKOUT_ENABLED || tier === "light" || Boolean(busy),
    },
    {
      id: "business",
      name: p.business?.name || "Business",
      price: p.business?.priceLabel || "要相談",
      badge: p.business?.badge,
      features: p.business?.features || [],
      contact: true,
      cta: paidCta("business", "contactSales", "お問い合わせ"),
      disabled: Boolean(busy),
    },
  ];

  const isPaid = tier === "light" || tier === "pro" || tier === "business";

  return (
    <div className="plan-modal__overlay" onClick={onClose}>
      <div
        className="plan-modal__dialog"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="plan-modal__close" onClick={onClose} aria-label="Close">
          <FiX />
        </button>
        <h2 className="plan-modal__title">{t.title || "プランを選択"}</h2>
        <p className="plan-modal__subtitle">
          {t.subtitle || "AI修正の月間回数に応じてプランを選べます。決済は Stripe で安全に処理されます。"}
        </p>
        <div className="plan-modal__grid">
          {cards.map((card) => (
            <div
              key={card.id}
              className={`plan-modal__card ${card.highlight ? "plan-modal__card--highlight" : ""} ${
                tier === card.id ? "plan-modal__card--current" : ""
              }`}
            >
              {card.badge ? <span className="plan-modal__badge">{card.badge}</span> : null}
              <h3 className="plan-modal__name">{card.name}</h3>
              <p className="plan-modal__price">{card.price}</p>
              <ul className="plan-modal__features">
                {card.features.map((f) => (
                  <li key={f}>
                    <FiCheck /> {f}
                  </li>
                ))}
              </ul>
              {card.contact ? (
                <a
                  className="plan-modal__cta"
                  href={`mailto:${CONTACT_EMAIL}?subject=FrontPlay%20Business%20plan`}
                >
                  {card.cta}
                </a>
              ) : (
                <button
                  type="button"
                  className="plan-modal__cta"
                  disabled={card.disabled}
                  onClick={() => {
                    if (isPaid && (TIER_RANK[tier] ?? 0) > (TIER_RANK[card.id] ?? 0) && card.id !== "free") {
                      handleManage();
                    } else {
                      handleSelect(card.id);
                    }
                  }}
                >
                  {busy === card.id ||
                  (busy === "portal" &&
                    isPaid &&
                    (TIER_RANK[tier] ?? 0) > (TIER_RANK[card.id] ?? 0) &&
                    card.id !== "free") ? (
                    <>
                      <FiLoader className="plan-modal__spin" /> {t.processing || "処理中…"}
                    </>
                  ) : (
                    card.cta
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
        {isLoggedIn ? (
          <button type="button" className="plan-modal__manage" onClick={() => { onClose?.(); openUsageModal(); }}>
            {texts.usage?.short || "利用状況を確認"}
          </button>
        ) : null}
        {isPaid ? (
          <button type="button" className="plan-modal__manage" onClick={handleManage} disabled={Boolean(busy)}>
            {busy === "portal" ? (
              <>
                <FiLoader className="plan-modal__spin" /> {t.processing || "処理中…"}
              </>
            ) : (
              t.manageSubscription || "請求・解約を管理"
            )}
          </button>
        ) : null}
        {error ? (
          <p className="plan-modal__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
